import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { apiClient } from '@/lib/api';
import { NotFoundError } from '@/lib/errors';
import { useAuthStore } from '@store/authStore';
import Button from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { RiShieldCheckLine, RiAlertLine, RiCheckboxCircleLine, RiQrCodeLine, RiKeyLine, RiArrowRightLine } from '@remixicon/react';
import { Alert, AlertDescription } from '@components/ui/Alert';

export default function MFAEnroll() {
  const navigate = useNavigate();
  const [mfaCode, setMfaCode] = useState('');
  const [mfaEnrollData, setMfaEnrollData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [enrolledFactorId, setEnrolledFactorId] = useState(null);
  const initialized = useRef(false);

  useEffect(() => {
    // Prevent double-execution in React Strict Mode
    if (initialized.current) return;
    initialized.current = true;

    initializeMFAEnrollment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeMFAEnrollment = async () => {
    setLoading(true);
    try {
      // Refresh session to ensure it's valid
      const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError || !session) {
        setError('Session expired. Please sign in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      // Check for existing MFA factors
      const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();

      if (!listError && factors && factors.totp && factors.totp.length > 0) {
        // Check if any factor is verified
        const verifiedFactor = factors.totp.find(f => f.status === 'verified');
        if (verifiedFactor) {
          setMessage('MFA is already set up for your account.');

          // Check if user has completed profile
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            try {
              await apiClient.getUserByUserId(user.id);
              setTimeout(() => navigate('/dashboard'), 1500);
            } catch (err) {
              if (err instanceof NotFoundError || err.statusCode === 404) {
                setTimeout(() => navigate('/profile-setup'), 1500);
              }
            }
          }
          setLoading(false);
          return;
        }

        // Found unverified factors - unenroll them first
        for (const factor of factors.totp) {
          if (factor.status !== 'verified') {
            try {
              const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
              if (unenrollError) {
                console.warn(`Failed to clean up existing MFA factor ${factor.id}:`, unenrollError.message);
                // Continue anyway - the factor might be stale
              }
            } catch (cleanupErr) {
              console.warn(`Error cleaning up MFA factor ${factor.id}:`, cleanupErr);
              // Continue anyway - we'll create a new one
            }
          }
        }
      }

      // Proceed with new enrollment (either no factors existed, or we cleaned up unverified ones)
      await enrollMFA();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const enrollMFA = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App',
      });

      if (error) {
        throw new Error(`MFA enrollment failed: ${error.message}`);
      }

      if (data) {
        setMfaEnrollData({
          qrCode: data.totp.qr_code,
          secret: data.totp.secret,
        });
        setEnrolledFactorId(data.id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyMFAEnrollment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!enrolledFactorId) {
        throw new Error('No factor ID found. Please re-enroll.');
      }

      // Validate code format
      const code = mfaCode.trim();
      if (!/^\d{6}$/.test(code)) {
        throw new Error('Please enter a valid 6-digit code');
      }

      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: enrolledFactorId,
      });

      if (challengeError) {
        throw new Error(`Challenge failed: ${challengeError.message}`);
      }

      if (!challengeData || !challengeData.id) {
        throw new Error('Failed to create challenge. Please try again.');
      }

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: enrolledFactorId,
        challengeId: challengeData.id,
        code: code,
      });

      if (verifyError) {
        throw new Error(`Verification failed: ${verifyError.message}`);
      }

      setMessage('MFA enrolled successfully!');

      // Refresh session to ensure token is available with AAL2
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        console.error("Error refreshing session after MFA:", refreshError);
      }

      // Wait a moment for the session to propagate
      await new Promise(resolve => setTimeout(resolve, 300));

      // Sync auth store to ensure API client has fresh token
      await useAuthStore.getState().syncWithSupabase();

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not found after MFA enrollment');
      }

      // Check if user has completed profile
      try {
        // Use getUserByUserId to check if current user has a profile
        await apiClient.getUserByUserId(user.id);
        // User profile exists in backend, go to dashboard
        navigate('/dashboard');
      } catch (err) {
        // Only redirect to profile setup if user profile doesn't exist (404)
        if (err instanceof NotFoundError || err.statusCode === 404) {
          navigate('/profile-setup');
        } else {
          // For other errors (network, server, etc.), throw to outer catch
          throw err;
        }
      }
    } catch (err) {
      setError(err.message);
      // Try to clean up the failed enrollment, but don't throw if it fails
      if (enrolledFactorId) {
        try {
          await supabase.auth.mfa.unenroll({ factorId: enrolledFactorId });
        } catch (unenrollErr) {
          console.error('Failed to unenroll factor after error:', unenrollErr);
          // Ignore unenroll errors - the factor might already be deleted
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
            <RiShieldCheckLine className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="text-3xl font-bold text-primary-500 mb-2">Setup Two-Factor Authentication</h2>
          <p className="text-gray-600">
            Secure your account with an authenticator app
          </p>
        </div>

        {error && (
          <Alert className="border-red-200 bg-red-50 mb-3">
            <RiAlertLine className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <p>MFA Setup Error</p>
              <p>{error}</p>
            </AlertDescription>
          </Alert>
        )}

        {message && (
          <Alert className="border-green-200 bg-green-50 mb-3">
            <RiCheckboxCircleLine className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              <p>Success</p>
              <p>{message}</p>
            </AlertDescription>
          </Alert>
        )}

        <div className="bg-white">
          {loading && !mfaEnrollData && (
            <div className="py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
              <p className="text-gray-600">Setting up MFA...</p>
            </div>
          )}

          {mfaEnrollData && (
            <div className="space-y-6">
              <div>
                <div className="flex items-center mb-4">
                  <RiQrCodeLine className="h-5 w-5 text-gray-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Scan QR Code</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Use your authenticator app (Google Authenticator, Authy, etc.) to scan this QR code:
                </p>
                <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block">
                  <img
                    src={mfaEnrollData.qrCode}
                    alt="QR Code for MFA Setup"
                    className="w-48 h-48"
                  />
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <RiKeyLine className="h-4 w-4 text-gray-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Manual Entry Key</span>
                </div>
                <p className="text-xs text-gray-600 mb-2">
                  If you can't scan the QR code, enter this key manually:
                </p>
                <div className="bg-white rounded border p-2">
                  <code className="text-sm font-mono text-gray-800 break-all">
                    {mfaEnrollData.secret}
                  </code>
                </div>
              </div>
            </div>
          )}
          {mfaEnrollData && (
            <form onSubmit={verifyMFAEnrollment} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700">
                  Verification Code <span className="text-red-500">*</span>
                </label>
                <Input
                  id="mfaCode"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  required
                  maxLength={6}
                  disabled={loading}
                  className="h-12 text-center text-lg font-mono tracking-widest bg-white border-2 border-gray-200 rounded-lg focus:outline-none focus:border-primary-500 disabled:opacity-50"
                />
                <p className="text-xs text-gray-500">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>
              
              <Button
                type="submit"
                disabled={loading || mfaCode.length !== 6}
                className="w-full h-12 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Verifying...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <RiShieldCheckLine className="mr-2 h-4 w-4" />
                    Verify & Enable MFA
                    <RiArrowRightLine className="ml-2 h-4 w-4" />
                  </div>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
